/*
 * MathGraph32 Javascript : Software for animating online dynamic mathematics figures
 * http://www.mathgraph32.org/
 * @Author Yves Biton (yves.biton@sesamath.net)
 * @License: GNU AGPLv3 https://www.gnu.org/licenses/agpl-3.0.html
 */
const NatObj = require('../types/NatObj')
const Vect = require('../types/Vect')
const kernel = require('../kernel/kernel')
const CPointAncetrePointsMobiles = require('./CPointAncetrePointsMobiles')
const CValeur = require('./CValeur')
module.exports = CPointBaseEnt

/**
 * Point libre à coordonnées entières dans un repère.
 * @constructor
 * @extends CPointAncetrePointsMobiles
 * @param {CListeObjets} listeProprietaire : La liste propriétaire de l'objet
 * @param {CImplementationProto} impProto : null ou pointe sur la construction propriétaire de l'objet.
 * @param {Boolean} estElementFinal : true si l'objet est un objet final de construction
 * @param {mtg32.Color} couleur : La couleur de l'objet
 * @param {Boolean} nomMasque : true si le nom de l'objet est masqué
 * @param {Integer} dec_x : Decalage horizontal du nom
 * @param {Integer} dec_y : Decalage vertical du nom
 * @param {Boolean} masque : true si l'objet est masque
 * @param {String} nom : Le nom eventuel de l'objet
 * @param {Integer} tailleNom : Indice de la taille du nom (voir mtg32.Fonte)
 * @param {mtg32.MotifPoint} motif : Le motif du point
 * @param {Boolean} marquePourTrace : true si le point est marqué pour la trace
 * @param {Boolean} pointLibre : true si le point n'est pas punaisé
 * @param {CRepere} rep : Le repère associé.
 * @param {Boolean} absLibres : true si le point est libre horizontalement
 * @param {Boolean} ordLibres : true si le point est libre verticalement
 * @param {mtg32.CValeurAssocieAVariable} absMin : abscisse mini quand le point n'est pas libre horizontalement
 * @param {mtg32.CValeurAssocieAVariable} absMax : abscisse maxi quand le point n'est pas libre horizontalement
 * @param {mtg32.CValeurAssocieAVariable} ordMin : ordonnée mini quand le point n'est pas libre verticalement
 * @param {mtg32.CValeurAssocieAVariable} ordMax : Ordonnée maxi quand le point n'est pas libre verticalement
 * @param {Number} abs : L'abscisse  (entière)
 * @param {Number} ord : L'ordonnée (entière)
 * @returns {undefined}
 */
function CPointBaseEnt (listeProprietaire, impProto, estElementFinal, couleur, nomMasque,
                               dec_x, dec_y, masque, nom, tailleNom, motif, marquePourTrace, pointLibre, rep, absLibres, ordLibres,
                               absMin, absMax, ordMin, ordMax, abs, ord) {
  if (arguments.length === 1) CPointAncetrePointsMobiles.call(this, listeProprietaire);
  else {
    CPointAncetrePointsMobiles.call(this, listeProprietaire, impProto, estElementFinal, couleur, nomMasque,
      dec_x, dec_y, masque, nom, tailleNom, motif, marquePourTrace, pointLibre);
    this.rep = rep;
    this.absLibres = absLibres;
    this.ordLibres = ordLibres;
    this.absMin = absMin;
    this.absMax = absMax;
    this.ordMin = ordMin;
    this.ordMax = ordMax;
    this.abs = abs;
    this.ord = ord;
  }
}
CPointBaseEnt.prototype = new CPointAncetrePointsMobiles();
CPointBaseEnt.prototype.constructor = CPointBaseEnt;
CPointBaseEnt.prototype.superClass = "CPointAncetrePointsMobiles";
CPointBaseEnt.prototype.className = "CPointBaseEnt";


CPointBaseEnt.prototype.getClone = function(listeSource, listeCible) {
  var ind1 = listeSource.indexOf(this.rep);
  var ind2 = listeSource.indexOf(this.impProto);
  var absMinClone = this.absMin.getClone(listeSource, listeCible);
  var absMaxClone = this.absMax.getClone(listeSource, listeCible);
  var ordMinClone = this.ordMin.getClone(listeSource, listeCible);
  var ordMaxClone = this.ordMax.getClone(listeSource, listeCible);

  return new CPointBaseEnt(listeCible, listeCible.get(ind2, "CImplementationProto"),
    this.estElementFinal, this.couleur, this.nomMasque, this.dec_x, this.dec_y, this.masque,
    this.nom, this.tailleNom, this.motif, this.marquePourTrace, this.pointLibre, listeCible.get(ind1, "CRepere"),
    this.absLibres, this.ordLibres, absMinClone, absMaxClone, ordMinClone, ordMaxClone, this.abs, this.ord);
};

CPointBaseEnt.prototype.getNature = function() {
  return NatObj.NPointBaseEnt;
};

CPointBaseEnt.prototype.initialisePourDependance = function() {
  CPointAncetrePointsMobiles.prototype.initialisePourDependance.call(this);
  if (!this.absLibres) {
    this.absMin.initialisePourDependance();
    this.absMax.initialisePourDependance();
  }
  if (!this.ordLibres) {
    this.ordMin.initialisePourDependance();
    this.ordMax.initialisePourDependance();
  }
};

CPointBaseEnt.prototype.dependDe = function(p) {
  if (this.elementTestePourDependDe === p) return this.dependDeElementTeste;
  var res = CPointAncetrePointsMobiles.prototype.dependDe.call(this, p) || this.rep.dependDe(p);
  if (!this.absLibres) res = res || this.absMin.dependDe(p) || this.absMax.dependDe(p);
  if (!this.ordLibres) res = res || this.ordMin.dependDe(p) || this.ordMax.dependDe(p);
  return this.memoriseDependance(res);
};

CPointBaseEnt.prototype.dependDePourBoucle = function(p) {
  return (p === this) || this.rep.dependDePourBoucle(p);
};

CPointBaseEnt.prototype.positionne = function(infoRandom, dimfen) {
  var min, max, absor, ordor
  this.existe = this.rep.existe;
  if (!this.existe) return;
  if (!this.absLibres) {
    this.absMin.positionne(infoRandom, dimfen);
    this.absMax.positionne(infoRandom, dimfen);
    this.existe = this.existe && this.absMin.existe && this.absMax.existe;
    if (this.existe) {
      min = this.absMin.rendValeur();
      max = this.absMax.rendValeur();
      absor = this.rep.abscisseOrigine.rendValeur();
      this.absmin =Math.round((min - absor)/this.rep.unitex);
      this.absmax = Math.round((max - absor)/this.rep.unitex);
      this.existe = (this.absmin <= this.absmax);
      if (this.existe) {
        if (this.abs < this.absmin) this.abs = this.absmin;
        else {
          if (this.abs > this.absmax) this.abs = this.absmax;
        }
      } else return;
    } else return;
  }
  if (!this.ordLibres) {
    this.ordMin.positionne(infoRandom, dimfen);
    this.ordMax.positionne(infoRandom, dimfen);
    this.existe = this.existe && this.ordMin.existe && this.ordMax.existe;
    if (this.existe) {
      min = this.ordMin.rendValeur();
      max = this.ordMax.rendValeur();
      ordor = this.rep.ordonneeOrigine.rendValeur();
      this.ordmin =Math.round((min - ordor)/this.rep.unitey);
      this.ordmax = Math.round((max - ordor)/this.rep.unitey);
      this.existe = (this.ordmin <= this.ordmax);
      if (this.existe) {
        if (this.ord < this.ordmin) this.ord = this.ordmin;
        else {
          if (this.ord > this.ordmax) this.ord = this.ordmax;
        }
      } else return;
    } else return;
  }
  var vec = new Vect();
  vec.x = this.abs*this.rep.u.x + this.ord*this.rep.v.x;
  vec.y = this.abs*this.rep.u.y + this.ord*this.rep.v.y;
  this.x = this.rep.o.x + vec.x;
  this.y = this.rep.o.y + vec.y;
  CPointAncetrePointsMobiles.prototype.positionne.call(this, infoRandom, dimfen);
};

CPointBaseEnt.prototype.testDeplacement = function(dimfen, xtest, ytest, pointr, abscr) {
  var x1 = new Array(4);
  var y1 = new Array(4);
  var xr, yr;

  if (!this.testFenetre(dimfen, xtest, ytest)) return false;
  var ux = this.rep.u.x;
  var uy = this.rep.u.y;
  var vx = this.rep.v.x;
  var vy = this.rep.v.y;
  var d = ux*vy-uy*vx;
  var ox = this.rep.o.x;
  var oy = this.rep.o.y;

  var x2 = xtest - ox;
  var y2 = ytest - oy;
  var ab = (vy*x2-vx*y2)/d;
  var or = (ux*y2-uy*x2)/d;
  var abint= Math.floor(ab);
  var orint = Math.floor(or);
  if ((ab-abint)<=0.5) xr = abint; else xr = abint+1;
  if ((or-orint)<=0.5) yr = orint; else yr = orint+1;
  var vec = new Vect();
  vec.x = xr*ux + yr*vx;
  vec.y = xr*uy + yr*vy;
  var xt = ox + vec.x;
  var yt = oy + vec.y;
  if (!this.testFenetre(dimfen,xt,yt)){
    x1[0] = abint;
    y1[0] = orint;
    x1[1] = abint+1;
    y1[1] = orint;
    x1[2] = abint+1;
    y1[2] = orint+1;
    x1[3] = abint;
    y1[3] = orint+1;
    var dis = 1; // La distance mini est au plus égale à 1
    var ind = 0;
    for (var i=0; i<4; i++) {
      if (this.testFenetre(dimfen,x1[i],y1[i])) {
        var d1 = kernel.distancePointPoint(ab,or,x1[i],y1[i]);
        if (d1<dis) {
          ind = i;
          dis = d1;
        }
      }
    }
    xr = x1[ind];
    yr = y1[ind];
  }
  if (!this.absLibres) {
    if (xr < this.absmin) xr = this.absmin;
    else {
      if (xr > this.absmax) xr = this.absmax;
    }
  }
  if (!this.ordLibres) {
    if (yr < this.ordmin) yr = this.ordmin;
    else {
      if (yr > this.ordmax) yr = this.ordmax;
    }
  }
  pointr.x = xr;
  pointr.y = yr;
  return true;
};

CPointBaseEnt.prototype.placeEn = function(xn, yn) {
  this.existe = true;
  this.abs = xn;
  this.ord = yn;
  this.horsEcran = !(this.testToile());
  this.positionneNom();
};

CPointBaseEnt.prototype.read = function(inps, list){
  CPointAncetrePointsMobiles.prototype.read.call(this, inps, list);
  this.absMin = new CValeur();
  this.absMax = new CValeur();
  this.ordMin = new CValeur();
  this.ordMax = new CValeur();
  var ind1 = inps.readInt();
  this.rep = list.get(ind1,"CRepere");
  this.abs = inps.readDouble();
  this.ord = inps.readDouble();
  this.absLibres = inps.readBoolean();
  this.ordLibres = inps.readBoolean();
  if (!this.absLibres) {
    this.absMin.read(inps, list);
    this.absMax.read(inps, list);
  }
  else {
    this.absMin.donneValeur(-5);
    this.absMax.donneValeur(5);
  }
  if (!this.ordLibres) {
    this.ordMin.read(inps, list);
    this.ordMax.read(inps, list);
  }
  else {
    this.ordMin.donneValeur(-5);
    this.ordMax.donneValeur(5);
  }
};

CPointBaseEnt.prototype.write =function(oups, list){
  CPointAncetrePointsMobiles.prototype.write.call(this, oups, list);
  var ind1 = list.indexOf(this.rep);
  oups.writeInt(ind1);
  oups.writeDouble(this.abs);
  oups.writeDouble(this.ord);
  oups.writeBoolean(this.absLibres);
  oups.writeBoolean(this.ordLibres);
  if (!this.absLibres) {
    this.absMin.write(oups, list);
    this.absMax.write(oups, list);
  }
  if (!this.ordLibres) {
    this.ordMin.write(oups, list);
    this.ordMax.write(oups, list);
  }
};



// WEBPACK FOOTER //
// ./src/objets/CPointBaseEnt.js